import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import for navigation
import {
    Plus,
    Trash2,
    Edit,
    FileText,
    Search,
    X,
    Eye,
    ChevronRight,
    Clock,
    BarChart3,
    Download
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function LetterTemplateMgmt() {
    const navigate = useNavigate();
    
    const categories = [
        { id: "offer-letter", name: "Offer Letter" },
        { id: "appointment-letter", name: "Appointment Letter" },
        { id: "relieving-letter", name: "Relieving Letter" },
        { id: "experience-letter", name: "Experience Letter" },
        { id: "increment-letter", name: "Increment Letter" },
        { id: "warning-letter", name: "Warning Letter" },
        { id: "custom", name: "Custom" }
    ];

    const [templates, setTemplates] = useState([
        // Example initial data
        { id: 1, title: "Standard Software Engineer Offer", category: "Offer Letter", catId: "offer-letter" }
    ]);
    
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [activeTab, setActiveTab] = useState('templates');
    const [activityLogs, setActivityLogs] = useState([]);
    const [reportType, setReportType] = useState('daily');
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });

    useEffect(() => {
        fetchActivityLogs();
    }, []);

    const fetchActivityLogs = async () => {
        try {
            // Mock data - replace with actual API call
            const mockLogs = [
                { id: 1, employeeId: 'EMP8383', action: 'Created', modifiedBy: 'HR Manager', date: '2024-01-15', details: 'Created new offer letter template' },
                { id: 2, employeeId: 'EMP8384', action: 'Updated', modifiedBy: 'Admin User', date: '2024-01-14', details: 'Modified appointment letter template content' },
                { id: 3, employeeId: 'EMP8385', action: 'Deleted', modifiedBy: 'System Admin', date: '2024-01-13', details: 'Removed outdated warning letter template' }
            ];
            setActivityLogs(mockLogs);
        } catch (error) {
            console.error('Failed to fetch activity logs:', error);
        }
    };

    const downloadExcel = (data, filename) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, `${filename}.xlsx`);
    };

    const downloadPDF = (data, filename, title) => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(title, 20, 20);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
        
        const tableData = data.map(item => Object.values(item));
        const headers = Object.keys(data[0] || {});
        
        doc.autoTable({
            head: [headers],
            body: tableData,
            startY: 40
        });
        
        doc.save(`${filename}.pdf`);
    };

    const generateReport = () => {
        let reportData = [];
        let filename = '';
        let title = '';
        
        if (activeTab === 'templates') {
            reportData = templates.map(template => ({
                'Template ID': template.id,
                'Title': template.title,
                'Category': template.category,
                'Last Modified': '2 hours ago',
                'Status': 'Active'
            }));
            filename = `letter-templates-${reportType}-${new Date().toISOString().split('T')[0]}`;
            title = `Letter Templates Report - ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        } else if (activeTab === 'logs') {
            reportData = activityLogs.map(log => ({
                'Employee ID': log.employeeId,
                'Action': log.action,
                'Modified By': log.modifiedBy,
                'Date': log.date,
                'Details': log.details
            }));
            filename = `template-activity-logs-${reportType}-${new Date().toISOString().split('T')[0]}`;
            title = `Template Activity Logs Report - ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        }
        
        return { reportData, filename, title };
    };

    const handleDownload = (format) => {
        const { reportData, filename, title } = generateReport();
        if (reportData.length === 0) {
            alert('No data available for download');
            return;
        }
        
        if (format === 'excel') {
            downloadExcel(reportData, filename);
        } else if (format === 'pdf') {
            downloadPDF(reportData, filename, title);
        }
    };

const handleNavigateToEditor = (catId, templateId = "new") => {
  // This now dynamically builds the URL based on the ID of the card clicked
  navigate(`/admin/${catId}/${templateId}`);
};

    const deleteTemplate = (id) => {
        if (!window.confirm("Delete this template?")) return;
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        fetchActivityLogs(); // Refresh logs after deletion
    };

    const filteredTemplates = templates.filter((t) => {
        const matchSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCategory = selectedCategory === "All" ? true : t.category === selectedCategory;
        return matchSearch && matchCategory;
    });

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">Letter Template Management</h1>
                    <p className="text-gray-500 text-sm mt-1">Create and manage official HR documentation.</p>
                </div>

                {activeTab === 'templates' && (
                    <div className="relative group">
                        <button className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700 transition-all">
                            <Plus className="mr-2" size={18} /> New Template
                        </button>
                        {/* Dropdown to pick category for new template */}
                        <div className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => handleNavigateToEditor(cat.id)}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 first:rounded-t-xl last:rounded-b-xl flex justify-between items-center"
                                >
                                    {cat.name} <ChevronRight size={14} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'templates'
                                    ? 'border-teal-500 text-teal-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <FileText className="w-5 h-5 inline-block mr-2" />
                            Templates
                        </button>
                        <button
                            onClick={() => setActiveTab('logs')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'logs'
                                    ? 'border-teal-500 text-teal-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <Clock className="w-5 h-5 inline-block mr-2" />
                            Activity Logs
                        </button>
                        <button
                            onClick={() => setActiveTab('reports')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'reports'
                                    ? 'border-teal-500 text-teal-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <BarChart3 className="w-5 h-5 inline-block mr-2" />
                            Reports
                        </button>
                    </nav>
                </div>
            </div>

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <>
                    {/* Search & Quick Filters */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="flex items-center bg-white shadow-sm border px-3 py-2 rounded-lg w-full md:w-1/3">
                            <Search size={18} className="text-gray-400 mr-2" />
                            <input
                                type="text"
                                placeholder="Search templates..."
                                className="outline-none w-full bg-transparent"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setSelectedCategory("All")}
                                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                                    selectedCategory === "All" ? "bg-teal-600 text-white shadow-md" : "bg-white border text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                All
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.name)}
                                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                                        selectedCategory === cat.name ? "bg-teal-600 text-white shadow-md" : "bg-white border text-gray-600 hover:bg-gray-50"
                                    }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Template Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredTemplates.map((t) => (
                            <div key={t.id} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 group">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 bg-teal-50 rounded-xl text-teal-600">
                                        <FileText size={24} />
                                    </div>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 bg-gray-50 px-2 py-1 rounded">
                                        {t.category}
                                    </span>
                                </div>

                                <h3 className="font-semibold text-gray-800 text-lg mb-1">{t.title}</h3>
                                <p className="text-xs text-gray-400 mb-6">Last modified: 2 hours ago</p>

                                <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
                                    <button 
                                        onClick={() => handleNavigateToEditor(t.catId, t.id)}
                                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-teal-600 hover:text-white transition-all"
                                    >
                                        <Edit size={16} /> Edit
                                    </button>
                                    <button 
                                        onClick={() => deleteTemplate(t.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        
                        {/* Quick Add Card */}
                        <button 
                            onClick={() => handleNavigateToEditor('custom')}
                            className="border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center p-6 text-gray-400 hover:border-teal-400 hover:text-teal-500 transition-all group"
                        >
                            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3 group-hover:bg-teal-50">
                                <Plus size={24} />
                            </div>
                            <span className="text-sm font-medium">Create Blank Template</span>
                        </button>
                    </div>

                    {/* Empty State */}
                    {filteredTemplates.length === 0 && searchQuery !== "" && (
                        <div className="text-center py-20">
                            <Search size={48} className="mx-auto text-gray-200 mb-4" />
                            <h3 className="text-gray-500 font-medium">No templates match your search</h3>
                        </div>
                    )}
                </>
            )}

            {/* Activity Logs Tab */}
            {activeTab === 'logs' && (
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Employee ID', 'Action', 'Modified By', 'Date', 'Details'].map(header => (
                                    <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {activityLogs.length > 0 ? (
                                activityLogs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-medium text-gray-900">{log.employeeId}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                log.action === 'Created' ? 'bg-green-100 text-green-800' :
                                                log.action === 'Updated' ? 'bg-blue-100 text-blue-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-700">{log.modifiedBy}</td>
                                        <td className="px-6 py-4 text-gray-700">{log.date}</td>
                                        <td className="px-6 py-4 text-gray-700">{log.details}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">No activity logs found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
                <div className="bg-white shadow-lg rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4">Generate Reports</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                            <select
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            >
                                <option value="daily">Daily</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="half-yearly">Half Yearly</option>
                                <option value="yearly">Yearly</option>
                                <option value="custom">Custom Date Range</option>
                            </select>
                        </div>
                        
                        {reportType === 'custom' && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        value={customDateRange.start}
                                        onChange={(e) => setCustomDateRange({...customDateRange, start: e.target.value})}
                                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                    <input
                                        type="date"
                                        value={customDateRange.end}
                                        onChange={(e) => setCustomDateRange({...customDateRange, end: e.target.value})}
                                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex gap-4">
                        <button
                            onClick={() => handleDownload('excel')}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
                        >
                            <Download className="w-5 h-5" />
                            Download Excel
                        </button>
                        <button
                            onClick={() => handleDownload('pdf')}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                        >
                            <Download className="w-5 h-5" />
                            Download PDF
                        </button>
                    </div>
                    
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-800 mb-2">Report Preview</h4>
                        <p className="text-sm text-gray-600">
                            Current tab: <span className="font-semibold">{activeTab === 'templates' ? 'Templates' : 'Activity Logs'}</span><br/>
                            Report type: <span className="font-semibold">{reportType.charAt(0).toUpperCase() + reportType.slice(1)}</span><br/>
                            {reportType === 'custom' && customDateRange.start && customDateRange.end && (
                                <>Date range: <span className="font-semibold">{customDateRange.start} to {customDateRange.end}</span><br/></>
                            )}
                            Records: <span className="font-semibold">{activeTab === 'templates' ? templates.length : activityLogs.length}</span>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}